/**
 * studio/src/lib/sentry-bootstrap.ts
 *
 * 2026-06-24: GlitchTip / Sentry error reporting for the Studio SPA.
 *
 * Imported as the FIRST module in `src/main.ts` (before
 * `gtm-bootstrap.ts`) so Sentry's `globalHandlers` integration is
 * installed before any other module can throw. Sentry auto-catches:
 *   - Uncaught errors via `window.addEventListener("error", ...)`
 *   - Unhandled promise rejections via `window.addEventListener(
 *     "unhandledrejection", ...)`
 * per the default `integrations` shipped by `@sentry/browser` (the
 * `globalHandlers` integration is on by default per the Sentry docs at
 * https://docs.sentry.io/platforms/javascript/configuration/options/#integrations).
 *
 * Why @sentry/browser (and not the Loader Script like web-public):
 *   - The Studio IS a real npm/Vite SPA, so adding the SDK is a normal
 *     dependency — Vite tree-shakes the unused integrations out of
 *     the bundle. The web-public static site does not have a
 *     bundler, so the Loader Script is the right path there.
 *   - The Studio is noindex (see `index.html`'s
 *     `<meta name="robots" content="noindex, nofollow" />`), so a
 *     lazy-loader pattern would actually be load-bearing. But Vite's
 *     tree-shaking gets us most of the benefit for free.
 *
 * DSN shape:
 *   The Sentry JS SDK is wire-protocol-compatible with GlitchTip
 *   (GlitchTip is a Sentry-API-compatible open-source error tracker,
 *   per https://glitchtip.com/). The DSN at
 *   `https://2d0951c9...@errors.activebusinesscore.com/32` is a
 *   public-by-design token that can only POST to the project's
 *   `/api/{projectId}/store/` endpoint — the same trust model as the
 *   web-public wiring (see honest-pitches-web-public's CHANGELOG
 *   entry on 2026-06-24 for the long-form explanation).
 *
 * When `VITE_GLITCHTIP_DSN` is unset (fresh clone without .env), this
 * module is a no-op — no Sentry.init, no network calls, no global
 * listeners. Pinned by `sentry-bootstrap.test.ts`.
 *
 * When the DSN is set and `import.meta.env.DEV` is true (Vite dev
 * server), `VITE_SENTRY_DEV` controls transport (default `intercept`):
 * log to the console via `beforeSend` and drop the envelope so the
 * tunnel is not POSTed. Set `VITE_SENTRY_DEV=live` to send from dev.
 *
 * Testability:
 *   The module reads `import.meta.env.*` at module-load time and
 *   caches the result in a private `INIT_OPTIONS`. Tests pass a
 *   custom `INIT_OPTIONS` via `__setInitOptionsForTests` (the leading
 *   double underscore signals "test-only", same convention used by
 *   `gtm-events.test.ts`). Production code never calls the setter.
 */
import * as Sentry from "@sentry/browser";
import type { ErrorEvent, EventHint } from "@sentry/core";
// Minimal structural type for the Feedback integration —
// @sentry/browser doesn't export `Integration` directly, but every
// integration has a `name: string` + arbitrary methods. We only need
// `createForm()` for the dialog-opener; the cast is contained.
type FeedbackIntegrationLike = {
  name: string;
  createForm?: (opts: Record<string, unknown>) => unknown;
};

export interface SentryInitOptions {
  dsn: string;
  environment: string;
  release: string;
}

/** Dev-only GlitchTip transport: intercept (console, no POST) or live. */
export type SentryDevMode = "intercept" | "live";

/**
 * Resolve how dev-mode Sentry events are handled.
 *
 * Production builds always use `live`. In dev (`import.meta.env.DEV`),
 * `VITE_SENTRY_DEV=live` sends to GlitchTip; anything else (including
 * unset) intercepts: log to the console and drop the envelope.
 */
export function resolveSentryDevMode(
  isDev: boolean,
  viteSentryDev: string | undefined,
): SentryDevMode {
  if (!isDev) return "live";
  const raw = (viteSentryDev ?? "intercept").trim().toLowerCase();
  return raw === "live" ? "live" : "intercept";
}

type SentryBeforeSend = (event: ErrorEvent, hint: EventHint) => ErrorEvent | null;

/**
 * When `intercept`, log the capture locally and return `null` so the SDK
 * does not POST to the tunnel (avoids dev-console noise from 403/CORS).
 */
export function createDevInterceptBeforeSend(
  devMode: SentryDevMode,
): SentryBeforeSend | undefined {
  if (devMode !== "intercept") return undefined;
  return (event, hint) => {
    const label =
      event.exception?.values?.[0]?.value ??
      (hint.originalException instanceof Error
        ? hint.originalException.message
        : String(hint.originalException ?? "capture"));
    // eslint-disable-next-line no-console -- dev-only GlitchTip intercept surface
    console.groupCollapsed(
      `%c[GlitchTip dev]%c ${label}`,
      "color:#f59e0b;font-weight:bold",
      "color:inherit",
    );
    // eslint-disable-next-line no-console -- dev-only GlitchTip intercept surface
    console.error(hint.originalException ?? event);
    const extra = event.extra;
    if (extra && typeof extra === "object" && Object.keys(extra).length > 0) {
      // eslint-disable-next-line no-console -- dev-only GlitchTip intercept surface
      console.table(extra);
    }
    // eslint-disable-next-line no-console -- dev-only GlitchTip intercept surface
    console.groupEnd();
    return null;
  };
}

// 2026-06-26: Feedback widget integration. The dialog is gated by
// `showFeedbackDialog(...)` (next to a user-visible save error), so
// `autoInject: false` is load-bearing — the launcher button MUST NOT
// appear on every page. Per the Sentry v10 API (@sentry/browser
// re-exports feedbackIntegration from @sentry/feedback per
// ./node_modules/@sentry/browser/build/npm/esm/prod/index.js line 2),
// the option names are documented at
// https://docs.sentry.io/platforms/javascript/user-feedback/configuration/.
// The integration instance registers with `name: "Feedback"` per
// @sentry/feedback/build/npm/esm/index.js line 579, so we look it up
// via `Sentry.getFeedback()` (re-exported by @sentry/browser from
// @sentry/feedback per the same index line 27).
const FEEDBACK_INTEGRATION_OPTIONS = {
  autoInject: false,
  triggerLabel: "Report a Bug",
  formTitle: "What were you doing when this broke?",
  submitButtonLabel: "Send Bug Report",
  cancelButtonLabel: "Cancel",
  successMessageText: "Thanks — the team will see this in GlitchTip.",
  enableScreenshot: false, // MVP: skip the screenshot path; it requires a hidden DOM host we don't want to manage.
} as const;

function getFeedbackIntegration(): FeedbackIntegrationLike | undefined {
  // 2026-06-26: per @sentry/feedback/build/npm/esm/index.js line 644
  // `getFeedback()` returns `client?.getIntegrationByName("Feedback")`.
  // We re-implement the lookup so we don't import from
  // @sentry/feedback directly (the canonical entry is
  // @sentry/browser per the .cursor/rules/prefer-official-sdks-and-clis.mdc
  // spirit — one SDK entry point).
  const client = Sentry.getClient();
  return (client?.getIntegrationByName?.("Feedback") as FeedbackIntegrationLike | undefined) ?? undefined;
}

const DEFAULT_OPTIONS: SentryInitOptions = {
  dsn: "",
  environment: "studio-dev",
  release: "honest-pitches-studio@dev",
};

let INIT_OPTIONS: SentryInitOptions = (() => {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  const dsn = env?.VITE_GLITCHTIP_DSN ?? "";
  const environment = env?.VITE_SITE_URL ?? "studio-dev";
  const release = `honest-pitches-studio@${env?.VITE_SITE_BUILD_STAMP ?? "dev"}`;
  return { dsn, environment, release };
})();

// 2026-06-27: route Sentry events through errors.honestpitches.com
// (the nginx tunnel — see HANDOFF-sentry-tunnel-2026-06-27.md).
// Bypasses uBlock / AdGuard / Brave Shields / EasyList filter lists
// that block the direct GlitchTip ingest URL. Per the Sentry docs at
// https://docs.sentry.io/platforms/javascript/troubleshooting/
// #using-the-tunnel-option the SDK's `tunnel` option rewrites the
// POST destination from the DSN host to the tunnel URL.
//
// CRITICAL: the SDK uses the tunnel URL EXACTLY as provided — it
// does NOT append `/api/{projectId}/envelope/`. Confirmed by reading
// `@sentry/core/src/api.ts#getEnvelopeEndpointWithUrlEncodedAuth`
// (see https://github.com/getsentry/sentry-javascript/commit/0acf10c):
// when `forceEnvelope()` is true (because tunnel is set), the function
// returns `this._tunnel` directly. The SDK unit test at the same
// commit pins this: `expect(getEnvelopeEndpointWithUrlEncodedAuth(
//   dsnPublicAPIWithTunnel.dsn, tunnel)).toEqual(tunnel)`. So the
// tunnel URL is the COMPLETE destination, not a base URL.
//
// Project 32 matches VITE_GLITCHTIP_DSN
// (`https://2d0951c96348418d8af375867a6c992c@errors.activebusinesscore.com/32`).
// The DSN itself is also embedded in the envelope header by the SDK,
// so the nginx tunnel can extract it and forward to the canonical
// `/api/{projectId}/envelope/` on the upstream side. The tunnel
// itself is a multi-project server block (see
// `infra/nginx-errors-tunnel.conf#ALLOWLIST_PROJECTS` — project 29 is
// the website's tunnel, project 32 is the Studio's). Pinned by the
// brief's reference nginx config + the contract test
// `infra/nginx-errors-tunnel.contract.test.mjs#ALLOWLIST`.
//
// 2026-06-28: dev-mode same-origin proxy. The Sentry docs explicitly
// recommend a RELATIVE tunnel URL because "Using a relative URL
// will not trigger a preflight CORS request, so no events will be
// blocked". In dev (Vite dev server, `import.meta.env.DEV === true`)
// the SDK POSTs to a relative path under the current origin
// (`/__sentry_tunnel/api/32/envelope/`); the Vite dev server's
// `server.proxy` block in vite.config.ts then forwards to the
// production tunnel host with the `/__sentry_tunnel` prefix
// stripped, preserving the upstream `/api/32/envelope/` path. This
// means the browser sees the request as same-origin (no CORS
// preflight, no ad-blocker interference) even though the upstream
// is the same production tunnel the deployed build uses. In a Vite
// production build, `import.meta.env.DEV` becomes `false` (Vite's
// build-time replacement), so the SDK uses the absolute production
// URL directly — same behavior as before this fix.
//
// The env var is `VITE_SENTRY_TUNNEL` (Vite exposes VITE_-prefixed
// vars to the client bundle). The fallback is the canonical prod
// tunnel URL — a fresh clone without `.env` ships the right default.
// Operators who run a staging tunnel can set
// `VITE_SENTRY_TUNNEL=https://errors.staging.honestpitches.com/api/32/envelope/`
// in `.env.production` to override without a code change.
const _viteEnv = (import.meta as unknown as {
  env: Record<string, string | boolean | undefined>;
}).env;
const SENTRY_TUNNEL = _viteEnv?.DEV === true
  ? "/__sentry_tunnel/api/32/envelope/"
  : (_viteEnv?.VITE_SENTRY_TUNNEL as string | undefined)
    ?? "https://errors.honestpitches.com/api/32/envelope/";

const SENTRY_DEV_MODE = resolveSentryDevMode(
  _viteEnv?.DEV === true,
  _viteEnv?.VITE_SENTRY_DEV as string | undefined,
);

function buildSentryInitPayload(options: SentryInitOptions) {
  const beforeSend = createDevInterceptBeforeSend(SENTRY_DEV_MODE);
  return {
    dsn: options.dsn,
    tunnel: SENTRY_TUNNEL,
    environment: options.environment,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    release: options.release,
    integrations: [Sentry.feedbackIntegration(FEEDBACK_INTEGRATION_OPTIONS)],
    ...(beforeSend ? { beforeSend } : {}),
  };
}

/**
 * Read-only view of the resolved init options. Used by
 * `reportError` to decide whether to forward to Sentry, and by
 * tests to assert the init shape. Reads from the mutable
 * `INIT_OPTIONS` so `__setInitOptionsForTests` is reflected.
 */
function getInitOptions(): SentryInitOptions {
  return INIT_OPTIONS;
}

let initialized = false;

/**
 * Read-only view of the Sentry init state. Used by `reportError`
 * and by tests. Returns the current value of `initialized`.
 */
function isSentryInitialized(): boolean {
  return initialized;
}

if (INIT_OPTIONS.dsn) {
  // SIDE-EFFECT: initialize the Sentry SDK + install global handlers.
  // Runs synchronously at module load so the first PFP event in the
  // funnel can also be the first Sentry capture if it throws.
  // 2026-06-26: the `integrations` array now includes the
  // `feedbackIntegration` so showFeedbackDialog(...) can open the
  // feedback dialog next to a save error. `autoInject: false` keeps
  // the launcher button off every page — the dialog is user-triggered
  // via the "Report this error" button in the save-error display.
  Sentry.init(buildSentryInitPayload(INIT_OPTIONS));
  initialized = true;
}

/**
 * Forward an error to GlitchTip with optional context.
 *
 * Thin wrapper over `Sentry.withScope` + `Sentry.captureException`
 * so call sites can attach `extra` / `tags` without each learning
 * the scope API. When `VITE_GLITCHTIP_DSN` is unset, this is a
 * no-op (returns false) so call sites don't have to guard.
 *
 * Usage:
 *   try {
 *     await riskyThing();
 *   } catch (e) {
 *     reportError(e, { route: route.name, action: "save-pitch" });
 *     throw e;
 *   }
 */
export function reportError(
  err: unknown,
  extra?: Record<string, unknown>,
): boolean {
  if (!initialized) return false;
  Sentry.withScope((scope) => {
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(err);
  });
  return true;
}

/**
 * 2026-07-09 (operator: "the user should never hear of slug"):
 * lightweight breadcrumb capture for diagnostic data that should
 * NOT generate a Sentry issue but SHOULD appear in the
 * breadcrumb trail so support can still see the raw server
 * message that the user-visible UI was rewritten away from.
 *
 * Use this for expected error shapes (validation rejects, slug
 * collisions, rate limits) where the friendly surface is the
 * right user-facing message but the underlying string is still
 * needed for the support runbook. Use `reportError` for
 * unexpected throws that should generate an alert.
 */
export function reportBreadcrumb(
  message: string,
  data: Record<string, unknown> = {},
): boolean {
  if (!initialized) return false;
  Sentry.withScope((scope) => {
    scope.setLevel("info");
    for (const [key, value] of Object.entries(data)) {
      scope.setExtra(key, value);
    }
    Sentry.addBreadcrumb({
      category: "publish-as-test",
      level: "info",
      message,
      data,
    });
  });
  return true;
}

/**
 * 2026-06-26: open the Sentry feedback dialog pre-populated with the
 * given error. Returns true when the dialog was opened; false when
 * Sentry is not initialized (dev mode / fresh clone / DSN unset).
 *
 * Used by the Studio save-error UI — the "Report this error" button
 * next to a `saveError.value` block calls this so the pitcher can
 * describe what they were doing when the save failed. The captured
 * error event is paired with the feedback submission via the
 * `eventId` thread — GlitchTip shows both on the issue page.
 *
 * Per @sentry/feedback's API (re-exported as `getFeedback` from
 * @sentry/browser per the prod index line 27), the feedback
 * integration instance is found via `getFeedback()`, and the dialog
 * is opened via `createForm()` (per the integration return at
 * @sentry/feedback/build/npm/esm/index.js line 609 — the integration
 * exposes `createForm`, `createWidget`, and `attachTo`; `createForm`
 * is the no-button-launched programmatic path).
 *
 * `eventId` wiring: per the Sentry docs, when the feedback is
 * submitted the user-supplied message + the captured error's eventId
 * are linked on the issue page. We capture the exception first so
 * the eventId exists before the dialog opens.
 */
export function showFeedbackDialog(
  err: unknown,
  extra?: Record<string, unknown>,
): boolean {
  if (!initialized) return false;
  const eventId = Sentry.withScope((scope) => {
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        scope.setExtra(key, value);
      }
    }
    scope.setLevel("error");
    return Sentry.captureException(err);
  });
  const feedback = getFeedbackIntegration();
  if (!feedback) return false;
  // The integration type from @sentry/browser doesn't expose
  // createForm/createWidget/attachTo in the public type — they're
  // attached to the instance at runtime. The FeedbackIntegrationLike
  // structural type above carries the method signature; we
  // runtime-guard the method presence (defensive: a future SDK
  // major could rename).
  if (typeof feedback.createForm !== "function") return false;
  feedback.createForm({ formSubmitted: false, eventId });
  return true;
}

/**
 * Test-only: override the init options before Sentry.init runs.
 *
 * Production code never calls this. The leading double-underscore
 * matches the gtm-events.test.ts convention. Call this from a test
 * `beforeAll` BEFORE importing `sentry-bootstrap.ts` (so the
 * module-load `if (INIT_OPTIONS.dsn)` block sees the override).
 *
 * Example:
 *   vi.resetModules();
 *   __setInitOptionsForTests({ dsn: "https://...@.../32", environment: "test", release: "studio@test" });
 *   await import("./sentry-bootstrap.js");
 */
export function __setInitOptionsForTests(opts: Partial<SentryInitOptions>): void {
  INIT_OPTIONS = { ...DEFAULT_OPTIONS, ...opts };
  if (INIT_OPTIONS.dsn) {
    Sentry.init(buildSentryInitPayload(INIT_OPTIONS));
    initialized = true;
  }
}

/**
 * Test-only: reset both `INIT_OPTIONS.dsn` and the `initialized`
 * flag back to the no-op state. Required when a test wants to
 * exercise the "DSN unset" path AFTER the module IIFE has already
 * fired Sentry.init from a `.env`-loaded DSN — vitest auto-loads
 * VITE_-prefixed vars from `.env` into `import.meta.env` at the
 * test-runner level, so the IIFE picks up the real DSN even when
 * the test wants the no-op path.
 *
 * Production code never calls this. Reset is a no-op when the
 * module's IIFE has not yet run (INIT_OPTIONS.dsn is already "").
 */
export function __resetForTests(): void {
  INIT_OPTIONS = { ...DEFAULT_OPTIONS };
  initialized = false;
}

export {
  isSentryInitialized as sentryInitialized,
  getInitOptions as sentryInitOptions,
  SENTRY_DEV_MODE as sentryDevMode,
};